import {NodeSpec, DOMOutputSpec, Attrs} from "prosemirror-model"
import {tableNodes} from "prosemirror-tables";

const
	blockquoteDOM: DOMOutputSpec = ["blockquote", 0],
	hrDOM: DOMOutputSpec = ["hr"],
	preDOM: DOMOutputSpec = ["pre", ["code", 0]],
	brDOM: DOMOutputSpec = ["br"];

const olDOM: DOMOutputSpec = ["ol", 0],
	ulDOM: DOMOutputSpec = ["ul", 0],
	liDOM: DOMOutputSpec = ["li", 0],
	dlDOM: DOMOutputSpec = ["dl", 0],
	dtDOM: DOMOutputSpec = ["dt", 0],
	ddDOM: DOMOutputSpec = ["dd", 0];




const nodes:{ [key:string]:NodeSpec } = {
	/// NodeSpec The top level document node.
	doc: {
		content: "block+"
	} as NodeSpec
};

/// A plain paragraph textblock. Represented in the DOM
/// as a `<p>` element.
nodes.paragraph = {
	content: "inline*",
	group: "block",
	attrs: {
		textAlign: { default: null },
		classes: { default: [] }
	},
	parseDOM: [ {
		tag: "p",
		getAttrs(node:HTMLElement) {
			const textAlign = node.style.textAlign || null;
			const classes:string[] = [];
			node.classList.forEach((cls) => classes.push(cls));
		    return { textAlign, classes };
		}
	}],
	toDOM(node) {
		const { textAlign, classes } = node.attrs;
		const pDOM:[string, ...any[]] = ["p"];
		const attrs: { [key: string]: string; } = {};
		if (textAlign) attrs.style = `text-align: ${textAlign}`;
		if (classes && classes.length>0) attrs.class = classes.join(' ');
		if (Object.keys(attrs).length > 0) pDOM.push(attrs);
		pDOM.push(0);
		return pDOM as DOMOutputSpec;
	}
};

/// A blockquote (`<blockquote>`) wrapping one or more blocks.
nodes.blockquote = {
	content: "block+",
	group: "block",
	defining: true,
	parseDOM: [{tag: "blockquote"}],
	toDOM() {
		return blockquoteDOM
	}
};

/// A horizontal rule (`<hr>`).
nodes.hr = {
	group: "block",
		parseDOM: [{tag: "hr"}],
		toDOM() {
		return hrDOM
	}
};


function headingAttributes(node:HTMLElement, level:number):Attrs {
	const classes:string[] = [];
	node.classList.forEach((cls) => classes.push(cls));

	return {
		level: 1,
		textAlign: node.style.textAlign || null,
		classes
	};
}

/// A heading textblock, with a `level` attribute that
/// should hold the number 1 to 6. Parsed and serialized as `<h1>` to
/// `<h6>` elements.
nodes.heading = {
	attrs: {
		level: {default: 1, validate: "number"},
		textAlign: { default: null },
		classes: { default: [] }
	},
	content: "inline*",
	group: "block",
	defining: true,
	parseDOM: [
		{
			tag: "h1",
			getAttrs(node) {
				return headingAttributes(node, 1);
			}
		},
		{
			tag: "h2",
			getAttrs(node) {
				return headingAttributes(node, 2);
			}
		},
		{
			tag: "h3",
			getAttrs(node) {
				return headingAttributes(node, 3);
			}
		},
		{
			tag: "h4",
			getAttrs(node) {
				return headingAttributes(node, 4);
			}
		},
		{
			tag: "h5",
			getAttrs(node) {
				return headingAttributes(node, 5);
			}
		},
		{
			tag: "h6",
			getAttrs(node) {
				return headingAttributes(node, 6);
			}
		}],
	toDOM(node) {
		const { textAlign, classes, level } = node.attrs;
		const hDOM:[string, ...any[]] = ["h" + level];
		const attrs: { [key: string]: string; } = {};
		if (textAlign) attrs.style = `text-align: ${textAlign}`;
		if (classes && classes.length>0) attrs.class = classes.join(' ');
		if (Object.keys(attrs).length > 0) hDOM.push(attrs);
		hDOM.push(0);
		return hDOM as DOMOutputSpec;
	}
};

/// A code listing. Disallows marks or non-text inline
/// nodes by default. Represented as a `<pre>` element with a
/// `<code>` element inside of it.
nodes.code_block = {
	content: "text*",
	marks: "link em strong underline strike mark fc bc",
	group: "block",
	code: true,
	defining: true,
	parseDOM: [{tag: "pre", preserveWhitespace: "full"}],
	toDOM() {
		return preDOM
	}
};

/// The text node.
nodes.text = {
	group: "inline"
};

/// An inline image (`<img>`) node. Supports `src`,
/// `alt`, and `href` attributes. The latter two default to the empty
/// string.
nodes.image = {
	inline: true,
	attrs: {
		src: {validate: "string"},
		alt: {default: null, validate: "string|null"},
		title: {default: null, validate: "string|null"},
		srcset: {default: null, validate: "string|null"},
		width: { default: null, validate: "string|null"},
		height: { default: null, validate: "string|null"},
		maxWidth: { default: null, validate: "string|null"},
		maxHeight: { default: null, validate: "string|null"},
	},
	group: "inline",
	draggable: true,
	parseDOM: [{
		tag: "img[src]", getAttrs(dom: HTMLElement) {
			const wstr = dom.style.width;
			const hstr = dom.style.height;
			const mwstr = dom.style.maxWidth;
			const mhstr = dom.style.maxHeight;
			return {
				src: dom.getAttribute("src"),
				title: dom.getAttribute("title"),
				alt: dom.getAttribute("alt"),
				srcset: dom.getAttribute("srcset"),
				width: wstr,
				height: hstr,
				maxWidth: mwstr,
				maxHeight: mhstr
			}
		}
	}],
	toDOM(node) {
		let {src, alt, title, srcset, width, height, maxWidth, maxHeight} = node.attrs;

		// Initialize attributes
		const attrs: Record<string, string> = { src, alt, title, srcset };

		// Conditionally add inline style if width or height is greater than 0
		const style: string[] = [];
		if (width) style.push(`width: ${width}`);
		if (height) style.push(`height: ${height}`);
		if (maxWidth) style.push(`max-width: ${maxWidth}`);
		if (maxHeight) style.push(`max-height: ${maxHeight}`);
		// Add the style attribute only if needed
		if (style.length > 0) {
			attrs.style = style.join("; ");
		}

		return ["img", attrs]
	}
};

/// A hard line break, represented in the DOM as `<br>`.
nodes.br = {
	inline: true,
	group: "inline",
	selectable: false,
	parseDOM: [{tag: "br"}],
	toDOM() {
		return brDOM
	}
};

nodes.ol = {
	content: "li+",
	group: "block",
	attrs: {
		order: { default: 1, validate: "number" },
		type: { default: "1", validate:"string" },
	},
	parseDOM: [{
		tag: "ol", getAttrs(dom: HTMLElement) {
			return {
				order: dom.hasAttribute("start") ? +dom.getAttribute("start")! : 1,
				type: dom.hasAttribute("type") ? dom.getAttribute("type") : "1"
			};
		}
	}],
	toDOM(node) {
		const domArray:[string, ...any[]] = ["ol"];
		const attrs = {};
		if (node.attrs.order !== 1) attrs["start"] = node.attrs.order;
		if (node.attrs.type && node.attrs.type !== "1") attrs["type"] = node.attrs.type;
		domArray.push(attrs);
		domArray.push(0);
		return domArray;
	}
};

nodes.ul = {
	content: "li+",
	group: "block",
	parseDOM: [{tag: "ul"}],
	toDOM() { return ulDOM }
};

nodes.li = {
	content: "paragraph block*",
	parseDOM: [{tag: "li"}],
	toDOM() { return liDOM },
	defining: true
};

nodes.dl = {
	group: "block",
	content: "(dt|dd)+",
	parseDOM: [{tag: "dl"}],
	toDOM() { return dlDOM }
};

nodes.dt = {
	content: "text*",
	parseDOM: [{tag: "dt"}],
	toDOM() { return dtDOM }
};

nodes.dd = {
	content: "inline*",
	parseDOM: [{tag: "dd"}],
	toDOM() { return ddDOM }
};

nodes.mention = {
	inline: true,
	atom: true,
	selectable: false,
	group: 'inline',
	marks: '_', // no marks inside
	attrs: {
		id: { default: '' },
		label: { default: '' },	// the text inside the mention
		title: { default: '' },	// title attribute of the link
		href: { default: null }, // optionally clickable
	},
	toDOM(node) {
		const { id, label, href, title } = node.attrs
		const attrs: Record<string, string> = {
			'data-mention': id,
			'class': 'mention',
			href: href || '#',
		};
		if (title) attrs.title = title;
		return ['a', attrs, label]
	},
	parseDOM: [{
		tag: 'a[data-mention]',
		getAttrs(dom) {
			const el = dom as HTMLAnchorElement;
			let url = el.getAttribute("href");
			if ('#' === url) url = null;
			let title = el.getAttribute("title");
			return {
				id: el.getAttribute('data-mention') || '',
				label: el.textContent || '',
				title: title == null ? '' : title,
				href: url
			};
		}
	}]
};

const tn = tableNodes({
	tableGroup: "block",
	cellContent: "block+",
	cellAttributes: {
		background: {
			default: null,
			getFromDOM(dom) {
				return dom.style.backgroundColor || null;
			},
			setDOMAttr(value, attrs) {
				if (value) attrs.style = (attrs.style || '') + `background-color: ${value};`;
			},
		}
	}
});

Object.assign(nodes, tn);	// copy nodes from tn to nodes

export default nodes;